import * as React from 'react';
import dayjs, {Dayjs} from 'dayjs';
import {DateInput} from '@deviceinsight/ng-ui-components';
import UserStore from '../../modules/common/UserStore';
import './DateTimeInput.less';

export type Props = {
	timestamp: string;
	minDate?: Date;
	maxDate?: Date;
	onChange: (timestamp: string | null | undefined) => void;
	step?: number;
	warning?: boolean;
	forceOpen?: boolean;
};

const formatTime = (date: string | Dayjs) => {
	const locale = UserStore.getUser()?.locale || 'en';
	return dayjs(date).locale(locale).format('HH:mm');
};

const roundToStep = (minutes: number, step: number): number => {
	return Math.round(minutes / step) * step;
};

const setTimeWithStep = (date: Dayjs, timeString: string, step: number): Dayjs => {
	const [hours, minutes] = timeString.split(':').map(Number);
	const totalMinutes = hours * 60 + minutes;
	const steppedMinutes = roundToStep(totalMinutes, step);
	const steppedHours = Math.floor(steppedMinutes / 60);
	const remainingMinutes = steppedMinutes % 60;

	return date.set('hour', steppedHours).set('minute', remainingMinutes).set('second', 0).set('millisecond', 0);
};

const validateDateTime = (date: Dayjs, minDate?: Date, maxDate?: Date): boolean => {
	if (minDate && date.isBefore(dayjs(minDate))) {
		return false;
	}
	if (maxDate && date.isAfter(dayjs(maxDate))) {
		return false;
	}
	return true;
};

const DateTimeInput: React.FC<Props> = ({timestamp, onChange, minDate, maxDate, step = 1, ...otherProps}) => {
	const [localDate, setLocalDate] = React.useState<Dayjs>(() => dayjs(timestamp));
	const [localTime, setLocalTime] = React.useState<string>(() => formatTime(timestamp));

	// Update local state when timestamp prop changes
	React.useEffect(() => {
		const newDate = dayjs(timestamp);
		setLocalDate(newDate);
		setLocalTime(formatTime(newDate));
	}, [timestamp]);

	const handleDateChange = React.useCallback(
		(newDateValue: number | string | null | undefined) => {
			if (!newDateValue) return;

			const newDate = dayjs(newDateValue);
			const updatedDateTime = setTimeWithStep(newDate, localTime, step);

			if (validateDateTime(updatedDateTime, minDate, maxDate)) {
				setLocalDate(newDate);
				onChange(updatedDateTime.toISOString());
			}
		},
		[localTime, step, minDate, maxDate, onChange]
	);

	const handleTimeChange = React.useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const newTime = event.target.value;
			setLocalTime(newTime);

			// Validate time format
			if (!/^\d{2}:\d{2}$/.test(newTime)) {
				return;
			}

			const updatedDateTime = setTimeWithStep(localDate, newTime, step);

			if (validateDateTime(updatedDateTime, minDate, maxDate)) {
				onChange(updatedDateTime.toISOString());
			}
		},
		[localDate, step, minDate, maxDate, onChange]
	);

	const handleTimeBlur = React.useCallback(() => {
		// Round to nearest step on blur
		const updatedDateTime = setTimeWithStep(localDate, localTime, step);
		const roundedTime = formatTime(updatedDateTime);

		if (roundedTime !== localTime) {
			setLocalTime(roundedTime);
		}

		if (validateDateTime(updatedDateTime, minDate, maxDate)) {
			onChange(updatedDateTime.toISOString());
		}
	}, [localDate, localTime, step, minDate, maxDate, onChange]);

	const handleKeyDown = React.useCallback(
		(event: React.KeyboardEvent<HTMLInputElement>) => {
			if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
				event.preventDefault();
				const direction = event.key === 'ArrowUp' ? 1 : -1;
				const currentDateTime = setTimeWithStep(localDate, localTime, step);
				const newDateTime = currentDateTime.add(direction * step, 'minute');

				if (validateDateTime(newDateTime, minDate, maxDate)) {
					const newTime = formatTime(newDateTime);
					setLocalTime(newTime);
					onChange(newDateTime.toISOString());
				}
			}
		},
		[localDate, localTime, step, minDate, maxDate, onChange]
	);

	const isValid = React.useMemo(() => {
		const currentDateTime = setTimeWithStep(localDate, localTime, step);
		return validateDateTime(currentDateTime, minDate, maxDate);
	}, [localDate, localTime, step, minDate, maxDate]);

	return (
		<span className="di date-time-input" data-testid="date-time-input">
			<span className={`date-wrapper ${isValid ? '' : 'invalid'}`}>
				<DateInput {...otherProps} value={localDate.valueOf()} onChange={handleDateChange} />
			</span>
			<input
				data-testid="time-input"
				type="text"
				value={localTime}
				onChange={handleTimeChange}
				onBlur={handleTimeBlur}
				onKeyDown={handleKeyDown}
				className={`time ${isValid ? '' : 'invalid'}`}
				placeholder="HH:MM"
				pattern="[0-9]{2}:[0-9]{2}"
				required
			/>
		</span>
	);
};

export default DateTimeInput;
